import React, { useCallback, useEffect, useState } from "react"
import ReactDOM from "react-dom"
import { GatsbyImage } from "gatsby-plugin-image"

// Utils
import { getImgixImageData, isClientSide } from "@utils"
import { logCustomGAEvent } from "../../../../_utils"
import { GA_CUSTOM_EVENTS } from "../../../../_store/constants"

// Shared Components
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@components/Button/Button"
import InterviewBuilderForm from "@components/HubspotForm/presets/InterviewBuilderForm/InterviewBuilderForm"

import Icon, { ICON_TYPES } from "@components/Icon/Icon"


// Store
import { Question } from "../../../../_store/types"

// Styled Components
import {
  ExportModalDIV,
  ExportModalOverlayBUTTON,
  ExportModalContentDIV,
} from "./ExportModal.styles"

type ExportModalProps = {
  onClickExport: () => void
  onClose: () => void
  selectedQuestions: Question[]
}

const HAS_CAPTURED_EMAIL_LS_KEY = "hs--interviewBuilder--hasCapturedEmail"

const ExportModal = ({
  onClickExport,
  onClose,
  selectedQuestions,
}: ExportModalProps) => {
  const [hasCapturedEmail, setHasCapturedEmail] = useState<boolean>(!!window.localStorage.getItem(HAS_CAPTURED_EMAIL_LS_KEY))
  const [hasCopiedToClipboard, setHasCopiedToClipboard] = useState<boolean>(false)
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const [modalEl, setModalEl] = useState<HTMLDivElement | null>(null)

  const closeModal = useCallback(() => {
    if (isClosing) return

    document.body.style.overflow = 'auto'
    setIsClosing(true)

    // Call `onClose` callback after animation is finished
    setTimeout(function () {
      if (onClose) onClose()
    }, 350)
  }, [isClosing, onClose])

  useEffect(() => {
    if (!isClientSide()) return

    const onKeyUp = (e: KeyboardEvent) => {
      // If the `esc` key is pressed:
      if (e.which === 27) closeModal()
    }

    const _modalEl = document.createElement('div')

    setModalEl(_modalEl)
    document.body.append(_modalEl)

    document.body.style.overflow = 'hidden'

    window.addEventListener('keyup', onKeyUp)

    setTimeout(() => {
      const inputEls = _modalEl.getElementsByTagName('input')
      if (inputEls.length) {
        inputEls[0].focus()
      } else {
        const buttonEls: HTMLCollectionOf<Element> = _modalEl.getElementsByClassName('Button')
        if (buttonEls && buttonEls.length) {
          (buttonEls[0] as HTMLButtonElement).focus()
        }
      }
    }, 500)

    return () => {
      document.body.removeChild(_modalEl)
      document.body.style.overflow = 'auto'
      window.removeEventListener('keyup', onKeyUp)
      setModalEl(null)
    }
  }, [closeModal])

  const handleFormSubmitted = () => {
    window.localStorage.setItem(HAS_CAPTURED_EMAIL_LS_KEY, "true")
    setHasCapturedEmail(true)
  }

  const handleClickCopyToClipboard = () => {
    if (hasCopiedToClipboard) return

    navigator.clipboard.writeText(
      selectedQuestions.map(({ textDefault, textEdited }) => textEdited || textDefault).join("\n\n")
    )

    setHasCopiedToClipboard(true)
  }

  useEffect(() => {
    if (!hasCopiedToClipboard) return

    const resetAlertTimeout = setTimeout(() => {
      setHasCopiedToClipboard(false)
    }, 3000)

    return () => {
      if (resetAlertTimeout) clearTimeout(resetAlertTimeout)
    }
  }, [hasCopiedToClipboard])

  return modalEl ?
    ReactDOM.createPortal(
      <ExportModalDIV
        className={`${isClosing ? "is-closing" : ""}`}
      >
        <ExportModalOverlayBUTTON onClick={closeModal} />
        <ExportModalContentDIV>
          <button
            className="close-button"
            onClick={closeModal}
            title="Close Modal"
          >
            <Icon type={ICON_TYPES["close"]} />
          </button>
          <GatsbyImage
            alt="Icon: Interview questions on a page of paper"
            className="icon"
            image={getImgixImageData(
              "/images/pages/interview-builder/InterviewBuilder--ExportModal--illo.png",
              100,
              1
            )}
          />
          {hasCapturedEmail ? (
            <>
              <h4>Access your interview questions</h4>
              <div className="actions">
                <Button
                  onClick={() => {
                    onClickExport()
                    logCustomGAEvent(GA_CUSTOM_EVENTS.SELECT_EXPORT_PDF, null)
                  }}
                  size={BUTTON_SIZES.L}
                >
                  Export PDF
                </Button>
                <Button
                  onClick={() => {
                    handleClickCopyToClipboard()
                    logCustomGAEvent(
                      GA_CUSTOM_EVENTS.SELECT_COPY_TO_CLIPBOARD,
                      null
                    )
                  }}
                  size={BUTTON_SIZES.L}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  {hasCopiedToClipboard ?
                    "Copied!" :
                    "Copy to Clipboard"
                  }
                </Button>
              </div>
            </>
          ) : (
            <>
              <h4>Enter your email to access your interview questions</h4>
              <div className="form-wrapper"> 
                <InterviewBuilderForm onSubmitted={handleFormSubmitted} />
              </div>
            </>
          )}
        </ExportModalContentDIV>
      </ExportModalDIV>
      , modalEl) :
    null
}

export default ExportModal
